

import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faFileAlt,
  faBook,
  faChartLine,
  faArrowUp,
  faArrowDown,
  faTachometerAlt,
} from "@fortawesome/free-solid-svg-icons";

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      label: "Total Team Members",
      value: "20",
      icon: faUsers,
      color: "from-blue-500 to-blue-600",
      change: "+12%",
      changeType: "increase",
      bgLight: "bg-blue-50",
    },
    {
      label: "Blog Posts",
      value: "10",
      icon: faBook,
      color: "from-green-500 to-green-600",
      change: "+8%",
      changeType: "increase",
      bgLight: "bg-green-50",
    },
    {
      label: "Case Studies",
      value: "15",
      icon: faFileAlt,
      color: "from-yellow-500 to-yellow-600",
      change: "+5%",
      changeType: "increase",
      bgLight: "bg-yellow-50",
    },
    {
      label: "Performance",
      value: "87%",
      icon: faChartLine,
      color: "from-purple-500 to-purple-600",
      change: "+3%",
      changeType: "increase",
      bgLight: "bg-purple-50",
    },
  ];


  const monthlyData = [
    { month: "Jan", performance: 65 },
    { month: "Feb", performance: 70 },
    { month: "Mar", performance: 75 },
    { month: "Apr", performance: 82 },
    { month: "May", performance: 87 },
    { month: "Jun", performance: 90 },
  ];



  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden group"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`bg-gradient-to-br ${item.color} p-3 rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-300`}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="w-6 h-6 text-white"
                  />
                </div>
                <span
                  className={`text-sm font-medium ${item.changeType === "increase" ? "text-green-600" : "text-red-600"} bg-${item.changeType === "increase" ? "green" : "red"}-50 px-2 py-1 rounded-full`}
                >
                  <FontAwesomeIcon
                    icon={
                      item.changeType === "increase" ? faArrowUp : faArrowDown
                    }
                    className="w-3 h-3 mr-1"
                  />
                  {item.change}
                </span>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-1">
                {item.value}
              </h3>
              <p className="text-gray-600 text-sm font-medium">{item.label}</p>
            </div>
            <div
              className={`h-1 bg-gradient-to-r ${item.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}
            ></div>
          </div>
        ))}
      </div>

      {/* Charts and Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Chart */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">
              Performance Overview
            </h3>
            <FontAwesomeIcon icon={faChartLine} className="text-gray-400" />
          </div>
          <div className="space-y-4">
            {monthlyData.map((data, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-600">
                    {data.month}
                  </span>
                  <span className="text-gray-600 font-semibold">
                    {data.performance}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500 relative"
                    style={{ width: `${data.performance}%` }}
                  >
                    <div className="absolute inset-0 bg-white opacity-20"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Quick Stats</h3>
            <FontAwesomeIcon icon={faTachometerAlt} className="text-gray-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Views</p>
              <p className="text-2xl font-bold text-gray-800">2,980</p>
              <p className="text-xs text-green-600 mt-1">↑ 23% this month</p>
            </div>
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Engagement Rate</p>
              <p className="text-2xl font-bold text-gray-800">76%</p>
              <p className="text-xs text-green-600 mt-1">↑ 5% this week</p>
            </div>
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Active Users</p>
              <p className="text-2xl font-bold text-gray-800">1,247</p>
              <p className="text-xs text-green-600 mt-1">↑ 12% today</p>
            </div>
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Bounce Rate</p>
              <p className="text-2xl font-bold text-gray-800">34%</p>
              <p className="text-xs text-red-600 mt-1">↓ 2% this week</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
